'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import ErrorCodeEnum from '@/enums/error-code-enum';
import { loadWithdrawalConfirmData } from '@/server-functions/withdrawal/withdrawal-actions';
import { WithdrawalConfirmData } from '@/server-functions/withdrawal/withdrawal-types';
import Container from '@/ui/components/shared/Container';
import Spinner from '@/ui/components/shared/Spinner/Spinner';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import formatPunctuation from '@/ui/utils/formatPunctuation';
import { buildTrackingId } from '@/utils/tracking-helper';
import { useWithdrawalStage } from '../withdrawal-stage';
import { WithdrawalConfirmButton } from '../withdrawal-confirm-button/withdrawal-confirm-button';
import { WithdrawalOrderOverview } from './withdrawal-order-overview';
import { WithdrawalConfirmTileType } from './types/withdrawal-confirm-tile-type';

type TileProps = Readonly<{ tile: WithdrawalConfirmTileType; trackingPrefix?: string }>;

// Final outcomes: the order will not become eligible again and an expired link stays expired.
// WITHDRAWAL_TEMPORARILY_UNAVAILABLE is deliberately missing: the customer retries a few
// minutes later.
const TERMINAL_WITHDRAWAL_ERRORS: ReadonlySet<ErrorCodeEnum> = new Set([
  ErrorCodeEnum.WITHDRAWAL_WINDOW_EXCEEDED,
  ErrorCodeEnum.WITHDRAWAL_ALREADY_REQUESTED,
  ErrorCodeEnum.WITHDRAWAL_NOT_ELIGIBLE,
  ErrorCodeEnum.WITHDRAWAL_LINK_EXPIRED,
  ErrorCodeEnum.WITHDRAWAL_COUNTRY_NOT_SUPPORTED,
]);

export function isTerminalWithdrawalError(errorCode?: ErrorCodeEnum) {
  return errorCode !== undefined && TERMINAL_WITHDRAWAL_ERRORS.has(errorCode);
}

export function getConfirmErrorText(tile: WithdrawalConfirmTileType, errorCode?: ErrorCodeEnum) {
  switch (errorCode) {
    case ErrorCodeEnum.WITHDRAWAL_LINK_EXPIRED:
      return tile['error-link-expired'];
    case ErrorCodeEnum.WITHDRAWAL_ALREADY_REQUESTED:
      return tile['error-already-withdrawn'];
    case ErrorCodeEnum.WITHDRAWAL_WINDOW_EXCEEDED:
    case ErrorCodeEnum.WITHDRAWAL_NOT_ELIGIBLE:
      return tile['error-not-eligible'];
    case ErrorCodeEnum.WITHDRAWAL_COUNTRY_NOT_SUPPORTED:
      return tile['error-country-not-supported'];
    case ErrorCodeEnum.WITHDRAWAL_TEMPORARILY_UNAVAILABLE:
      return tile['error-temporarily-unavailable'];
    default:
      return tile['error-generic'];
  }
}

function useConfirmData(token: string) {
  const [data, setData] = useState<WithdrawalConfirmData | null>(null);
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (hasFiredRef.current) {
      return;
    }
    hasFiredRef.current = true;

    loadWithdrawalConfirmData(token)
      .then(setData)
      .catch(() => setData({ status: 'error' }));
  }, [token]);

  return data;
}

function WithdrawalConfirmContent({ tile, token, trackingPrefix }: TileProps & { token: string }) {
  const t = useTranslations('OrderDetails');
  const data = useConfirmData(token);
  const [confirmError, setConfirmError] = useState<{ errorCode?: ErrorCodeEnum } | null>(null);

  const [emailNoteBefore, emailNoteAfter = ''] = t(
    'The withdrawal confirmation will be sent to %s',
  ).split('%s');

  if (data === null) {
    return (
      <output className="flex justify-center py-10" aria-live="polite">
        <Spinner className="h-10 w-10" />
      </output>
    );
  }

  if (data.status === 'error') {
    return <RichTextRenderer blocks={getConfirmErrorText(tile, data.errorCode)} />;
  }

  // A final confirm error ends the confirm stage: a retry would only push tracking
  // and call the backend again for the same outcome.
  if (confirmError && isTerminalWithdrawalError(confirmError.errorCode)) {
    return <RichTextRenderer blocks={getConfirmErrorText(tile, confirmError.errorCode)} />;
  }

  return (
    <div className="space-y-8">
      {(tile['description-headline']?.text || tile['description-text']) && (
        <div className="space-y-2">
          {tile['description-headline']?.text && (
            <h2 className="text-lg font-bold">{tile['description-headline'].text}</h2>
          )}
          <RichTextRenderer blocks={tile['description-text']} />
        </div>
      )}
      <WithdrawalOrderOverview order={data} />
      <p>
        {emailNoteBefore}
        <strong className="underline">{data.email}</strong>
        {emailNoteAfter}
      </p>
      <WithdrawalConfirmButton
        token={token}
        source={data.source}
        buttonText={tile['button-text']?.text}
        dialogTexts={{
          headline: tile['dialog-headline']?.text,
          text: tile['dialog-text'],
          confirmLabel: tile['dialog-confirm-label']?.text,
          cancelLabel: tile['dialog-cancel-label']?.text,
        }}
        onError={(errorCode) => setConfirmError({ errorCode })}
        trackingId={buildTrackingId(trackingPrefix, 'confirm')}
      />
      {confirmError && (
        <div role="alert" className="text-sm text-red">
          <RichTextRenderer blocks={getConfirmErrorText(tile, confirmError.errorCode)} />
        </div>
      )}
    </div>
  );
}

export function WithdrawalConfirmTile({ tile, trackingPrefix }: TileProps) {
  const { stage, token } = useWithdrawalStage();

  if (stage !== 'confirm' || !token) {
    return null;
  }

  return (
    <Container>
      <div className="mx-auto max-w-4xl space-y-8">
        <div className="space-y-3 text-center">
          <h1 className="text-3xl font-bold sm:text-4xl">
            {formatPunctuation(tile.headline?.text)}
          </h1>
          <RichTextRenderer blocks={tile.subline} className="text-md" />
        </div>
        <WithdrawalConfirmContent
          key={token}
          tile={tile}
          token={token}
          trackingPrefix={trackingPrefix}
        />
      </div>
    </Container>
  );
}
